import { Injectable } from '@angular/core';
import { Country } from '../../model/country.model';
import { Region } from '../../model/region.model';
import { District } from '../../model/district.model';
import { SearchService } from '../../app/web/components/searchbar/search.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { ViewStatus } from '@uranda/ui';
import { Partner } from '../../app/models/partner.model';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class SearchBarService {
  constructor(
    private searchService: SearchService,
    private router: Router,
    private translateService: TranslateService
  ) {
    // `name` è la CHIAVE i18n, non il testo tradotto: il service è un singleton root costruito
    // prima che il JSON della lingua sia caricato (instant() restituirebbe la chiave inglese) e
    // non seguirebbe i cambi lingua. La traduzione avviene nei template col pipe translate.
    this.sorts = [
      {
        name: 'Highest Rated',
        orderBy: 'averageRate',
        direction: 'desc',
        icon: 'ti ti-star-filled',
      },
      {
        name: 'Lowest Rated',
        orderBy: 'averageRate',
        direction: 'asc',
        icon: 'ti ti-star',
      },
      {
        name: 'Most Reviews',
        orderBy: 'reviewsCount',
        direction: 'desc',
        icon: 'ti ti-messages',
      },
      {
        name: 'Least Reviews',
        orderBy: 'reviewsCount',
        direction: 'asc',
        icon: 'ti ti-message-dots',
      },
    ];

    this.languages = [
      { name: 'all_f', code: 'wl' },
      { name: 'English', code: 'gb' },
      { name: 'Italian', code: 'it' },
      { name: 'Albanian', code: 'al' },
      { name: 'Spanish', code: 'es' },
      { name: 'French', code: 'fr' },
      { name: 'German', code: 'de' },
      { name: 'Portuguese', code: 'pt' },
      { name: 'Russian', code: 'ru' },
    ];
  }

  searchPageStatus = ViewStatus.LOADING;
  allCountries: Country[] = [];
  allRegions: Region[] = [];
  allDistricts: District[] = [];
  selectedCountry?: Country;
  selectedRegion?: Region;
  selectedDistrict?: District;
  pageNumber = 0;
  pageSize = 10;
  hasMore = true;
  totalElements = 0;
  loadingMore = false;
  selectedLanguage: string = 'wl';
  clinicCount: number = 0;
  partners?: Partner[];
  private closeOffcanvasSubject = new Subject<void>();
  closeOffcanvas$ = this.closeOffcanvasSubject.asObservable();
  private dataReadySubject = new BehaviorSubject<boolean>(false);
  dataReady$ = this.dataReadySubject.asObservable();
  searchbarOffcanvasRef: any;
  dataReady = false;
  selectedSort: string = 'averageRate';
  selectedDirection: string = 'desc';
  isStuck = false;
  offsetTop = 0;
  sorts: { name: string; orderBy: string; direction: string; icon: string }[] =
    [];
  languages: { name: string; code: string }[] = [];

  async loadDefaultData() {
    this.allCountries = await this.searchService.getCountries();
    this.selectedCountry = this.allCountries[0];
    this.allRegions = await this.searchService.getRegions(
      this.selectedCountry.id
    );
    this.allDistricts = await this.searchService.getDistrict(
      this.allRegions[0].id
    );
  }

  async search() {
    await this.loadPartners(false);
  }

  async loadNextPage() {
    await this.loadPartners(true);
  }

  async loadPartners(append = false) {
    if (append && (this.loadingMore || !this.hasMore)) return;

    try {
      if (!append) {
        this.searchPageStatus = ViewStatus.LOADING;
        this.pageNumber = 0;
      } else {
        this.loadingMore = true;
        this.pageNumber += 1;
      }

      const params = this.getSearchParams();
      const data = await this.fetchPartnersAndUpdate(params);

      this.partners = append ? [...(this.partners ?? []), ...data] : data;

      this.clinicCount = this.partners?.length ?? 0;
      this.hasMore = (this.partners?.length ?? 0) < this.totalElements;

      this.searchPageStatus = ViewStatus.READY;
    } catch (error) {
      console.error('Error loading search results:', error);
      this.hasMore = false;
      this.searchPageStatus = ViewStatus.READY;
    } finally {
      this.loadingMore = false;
    }
  }

  private getSearchParams(): {
    page: number;
    size: number;
    countryId?: string;
    regionId?: string;
    districtId?: string;
    language?: string;
    sortBy?: string;
    sortOrder?: string;
  } {
    let languageSelected = this.selectedLanguage;

    if (languageSelected === 'wl') {
      languageSelected = '';
    }
    return {
      page: this.pageNumber,
      size: this.pageSize,
      countryId: this.selectedCountry?.id,
      regionId: this.selectedRegion?.id,
      districtId: this.selectedDistrict?.id,
      language: languageSelected,
      sortBy: this?.selectedSort,
      sortOrder: this?.selectedDirection,
    };
  }

  private async fetchPartnersAndUpdate(
    params: ReturnType<typeof this.getSearchParams>
  ) {
    const response = await this.searchService.getPartners(
      params.page,
      params.size,
      params.countryId,
      params.regionId,
      params.districtId,
      params.language,
      params.sortBy,
      params.sortOrder
    );

    if (params.language === '') {
      this.selectedLanguage = 'wl';
    }

    this.totalElements = response.totalElements;
    this.searchService.searchResult = response.search;
    this.searchService.onSearchReady.emit(true);

    return response.data ?? [];
  }

  closeSearchbarOffcanvas() {
    if (this.searchbarOffcanvasRef) {
      this.searchbarOffcanvasRef.close();
    }
  }

  triggerCloseOffcanvas() {
    this.closeOffcanvasSubject.next();
  }

  triggerDataReady() {
    this.dataReadySubject.next(true);
  }

  onSortChange(code: any): void {
    this.selectedSort = code.orderBy;
    this.selectedDirection = code.direction;
    this.router.navigate([], {
      queryParams: { sort: this.selectedSort + '-' + this.selectedDirection },
      queryParamsHandling: 'merge',
    });
  }
}